import { useCallback, useEffect, useMemo, useRef } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { CUSTOMER_DATA_TABS, LEAD_DETAILS_FORMS, REAL_ESTATE_USAGES } from 'cpool-constants';
import type { SchemaLoanRequestResolver_Get } from 'cpool-tables/backend-resolvers';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { hide } from '~/features/leadDetails/components/Overview/CustomerData/General/utils/hide.ts';
import {
    syncRealEstateBuildingDescription,
    syncRealEstateBuildingDescriptions,
} from '~/features/leadDetails/components/Overview/CustomerData/RealEstates/buildingDescriptionTransfer';
import RealEstateFields from '~/features/leadDetails/components/Overview/CustomerData/RealEstates/RealEstateFields.tsx';
import { useCustomerDataTabsContext } from '~/features/leadDetails/context/CustomerDataTabsContext.tsx';
import { useLeadDetailsFormContext } from '~/features/leadDetails/context/LeadDetailsFormContext.tsx';
import { useTabErrorNotification } from '~/features/leadDetails/hooks/useTabErrorNotification.ts';
import type { SchemaRealEstatesForm } from '~/features/leadDetails/schemas/realEstates.schema.ts';
import { schemaRealEstatesForm } from '~/features/leadDetails/schemas/realEstates.schema.ts';
import { generateRealEstateCommands } from '~/features/leadDetails/utils/generateRealEstateCommands.ts';
import { submitCommands } from '~/features/leadDetails/utils/submitCommands.ts';
import { useOnMountAndChange } from '~/hooks/useOnMountAndChange.tsx';

type Props = {
    lead: SchemaLoanRequestResolver_Get;
};

const formId = LEAD_DETAILS_FORMS.REAL_ESTATE;

const RealEstates: React.FC<Props> = ({ lead }) => {
    const { t } = useTranslation();
    const { registerDirtyState, registerReset, registerSubmit } = useLeadDetailsFormContext();
    const { tab } = useCustomerDataTabsContext();
    const defaultValues = useMemo(
        () => syncRealEstateBuildingDescriptions({ immobilien: lead.haushalt.immobilien }),
        [lead.haushalt.immobilien],
    );

    const formMethods = useForm<SchemaRealEstatesForm>({
        defaultValues,
        mode: 'onChange',
        resolver: zodResolver(schemaRealEstatesForm),
    });
    const resetRealEstates = useCallback<typeof formMethods.reset>(
        (values, options) => {
            if (typeof values === 'function') {
                formMethods.reset(
                    (currentValues) => syncRealEstateBuildingDescriptions(values(currentValues)),
                    options,
                );
                return;
            }
            if (values == null) {
                formMethods.reset(values, options);
                return;
            }
            formMethods.reset(
                {
                    ...values,
                    immobilien: values.immobilien?.map((immobilie) =>
                        immobilie == null ? immobilie : syncRealEstateBuildingDescription(immobilie),
                    ),
                },
                options,
            );
        },
        [formMethods],
    );

    useTabErrorNotification(CUSTOMER_DATA_TABS.REAL_ESTATE, formMethods.formState.errors);

    // NOTE: we need to set the default values for the antragsteller2 field when the second applicant is added to the form
    const hadAntragsteller2 = useRef(!!lead.antragsteller2);
    useEffect(() => {
        if (!hadAntragsteller2.current && lead.antragsteller2) {
            hadAntragsteller2.current = true;
            formMethods.reset(defaultValues);
        }
    }, [defaultValues, lead.antragsteller2, formMethods]);

    useEffect(() => {
        registerDirtyState(formId, {
            dirty: formMethods.formState.isDirty,
            dirtyFields: formMethods.formState.dirtyFields,
        });
    }, [registerDirtyState, formMethods.formState, formMethods.formState.isDirty]);

    registerReset(formId, resetRealEstates);

    useOnMountAndChange(() => {
        registerSubmit(
            (setSubmitStatus) =>
                formMethods.handleSubmit(
                    async (data) => {
                        // we remove all "vermieteteWohnflaeche" if the "nutzungsart" is not "EIGENGENUTZT_UND_VERMIETET"
                        // eslint-disable-next-line no-param-reassign
                        data.immobilien = data.immobilien
                            .map((immobilie, index) => {
                                if (
                                    immobilie.vermieteteWohnflaeche &&
                                    immobilie.wohnflaeche &&
                                    immobilie.vermieteteWohnflaeche > immobilie.wohnflaeche
                                ) {
                                    formMethods.setError(`immobilien.${index}.vermieteteWohnflaeche`, {
                                        message: t('REAL_ESTATE.TOASTS.ERROR_VERMIETETE_WOHNFLAECHE_TOO_HIGH'),
                                        type: 'invalid',
                                    });
                                    setSubmitStatus({
                                        changes: true,
                                        error: new Error(t('REAL_ESTATE.TOASTS.ERROR_VERMIETETE_WOHNFLAECHE_TOO_HIGH')),
                                        saved: false,
                                    });
                                    return immobilie;
                                }
                                if (
                                    immobilie.vermieteteWohnflaeche &&
                                    immobilie.nutzungsart !== REAL_ESTATE_USAGES.EIGENGENUTZT_UND_VERMIETET
                                ) {
                                    formMethods.setValue(`immobilien.${index}.vermieteteWohnflaeche`, null, {
                                        shouldDirty: false,
                                    });
                                    return {
                                        ...immobilie,
                                        vermieteteWohnflaeche: null,
                                    };
                                }
                                return immobilie;
                            })
                            .map(syncRealEstateBuildingDescription);
                        const commands = generateRealEstateCommands({ immobilien: lead.haushalt.immobilien }, data);
                        await submitCommands(lead.id, setSubmitStatus, commands, formMethods.reset);
                    },
                    (error) => {
                        setSubmitStatus({ changes: true, error, saved: false });
                    },
                ),
            formId,
            formMethods.trigger,
        );
    }, [formMethods, lead.haushalt, lead.id, registerSubmit, t]);

    return (
        <FormProvider {...formMethods}>
            <form className="mb-2" name={formId} style={hide(tab !== CUSTOMER_DATA_TABS.REAL_ESTATE)}>
                <RealEstateFields lead={lead} />
                {lead.coApplicantHidden && (
                    <div className="flex justify-center my-6">
                        <span className="text-gray-400">{t('OVERVIEW.SECOND_APPLICANT_HIDDEN')}</span>
                    </div>
                )}
            </form>
        </FormProvider>
    );
};

export default RealEstates;
